// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';
/**
 * <Tableau> — LE tableau de registre de BIO24 (L0, 10/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Successeur de `ged/src/components/TableauTriable.tsx` (RQ, 19/08/2026), dont
 * il garde l'API, le DOM et les leçons : tri sur la donnée BRUTE, trois états
 * au clic, en-tête collant MESURÉ (`enTeteCollant`), colonnes déplaçables et
 * redimensionnables, dépli de ligne, clavier intégré, `.ged-tableau` /
 * `ged-row` / `CelluleLien` / `LigneLien` / `SansOuvrir`, plancher de 44 px.
 * L'alias `TableauTriable` a été supprimé à la fin de L8 (11/10/2026) : on importe `Tableau`.
 *
 * ── Ce que L0 ajoute (AUDIT-TABLES §6, PROMPT-TABLEAUX §1) ────────────────
 *
 *  · `@tanstack/react-table` EN MOTEUR (tri, pagination, sélection) — headless :
 *    il calcule, le DOM reste le nôtre. `@tanstack/react-virtual` au-delà de
 *    200 lignes non paginées (É-14).
 *  · Trois NIVEAUX (`simple` / `standard` / `avance`, voir `tableauMoteur.ts`).
 *    ⚠️ Sans `niveau`, le comportement est celui de `TableauTriable` avant L0,
 *    au pixel : les 25 appels existants ne changent pas tant que leur lot ne
 *    les configure pas.
 *  · Masquage (menu « Colonnes »), épinglage à gauche (`insetInlineStart`,
 *    É-11), filtres PAR colonne selon `type` (texte, liste, dates, nombres),
 *    sélection et actions PAR LOT (une entrée de journal par ligne, pas de
 *    suppression), export de RÉFÉRENCE (`exportRegistre.ts`).
 *  · États INTÉGRÉS (É-13) : `chargement` → squelette, `erreur` → message +
 *    « Réessayer », puis seulement l'état vide. Dire « aucun » avant d'avoir
 *    reçu la réponse est impossible par construction.
 *  · Deux DENSITÉS en jetons (É-3) : aucune hauteur, aucun padding, aucune
 *    taille de police de cellule en dur dans ce fichier.
 *  · Colonne d'ACTIONS toujours dernière, intitulée « Actions », avec
 *    `SansOuvrir` automatique quand la ligne s'ouvre (É-9).
 *  · `aria-sort` sur le `<th>`, `'none'` sur une colonne triable non triée (É-7).
 *  · `type: 'nombre'` → aligné à droite, chiffres à chasse fixe (É-4).
 *
 * ── Ce que ce fichier NE fait pas ──────────────────────────────────────────
 *
 *  · Il ne formate aucune date : `rendu` passe par `fmtDate` (contrôle statique
 *    `qualite-tableau-dates` sur `type: 'date'`). Les puces de filtre de date
 *    écrivent jj/mm/aaaa depuis l'ISO, sans dépendre d'une application.
 *  · Les préférences (ordre, masquées, épinglées, largeurs, tri) vont EN BASE
 *    depuis L1 — une ligne par (personne, tableau), écarts au défaut seulement,
 *    par `tableauPrefs.ts` ; le `localStorage` (`bio24.tableau.<id>`) n'est
 *    que leur cache, et suffit en cas de panne. Le composant ne touche jamais
 *    le navigateur lui-même : cache et serveur sont injectés.
 *
 * Partagé par `shared/sync.sh` : n'importer que `react`, les deux paquets
 * TanStack et les voisins de `shared/ui/`.
 */
import React from 'react';
import {
  useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel,
  type ColumnDef, type SortingState, type RowSelectionState, type PaginationState,
} from '@tanstack/react-table';
import { useVirtualizer, useWindowVirtualizer } from '@tanstack/react-virtual';
import { Bouton } from './Bouton';
import { Icon } from './Icon';
import { telechargerExport } from './exportRegistre';
import { chargerPref, ecrireCache, prefDuCache, migrerPrefsLocales } from './tableauPrefs';
import { depotPrefs, cacheNavigateur } from './tableauDepotPrefs';
import {
  capacites, ecartsPref, colonnesOrdonnees, colonnesVisibles, deposerColonne, deplacerColonne,
  basculerColonne, basculerEpingle, estPersonnalisee, colSpanDetail, decalagesEpingles, doitVirtualiser,
  mesurerLigneEtDetail, comparerValeurs, estVide, passeFiltres, filtreActif, decrireFiltre, valeursDistinctes,
  executerParLot, libelleConfirmationLot, libelleBilan, motifLotValide, colonnesExportables, bornerLargeur,
  PREF_VIDE, LARGEUR_MIN,
  type Niveau, type Densite, type TypeColonne, type PrefTableau, type FiltreColonne, type ActionLot, type BilanLot,
  type RangeeMesurable,
} from './tableauMoteur';

export type { Niveau, Densite, TypeColonne, FiltreColonne, ActionLot, BilanLot } from './tableauMoteur';

export interface ColonneTriable<L> {
  cle: string;
  libelle: React.ReactNode;
  /** Valeur de TRI (et de filtre) — brute. Absente = colonne non triable (actions, cases). */
  valeur?: (ligne: L) => string | number | null | undefined;
  rendu: (ligne: L) => React.ReactNode;
  /** Style de cellule additionnel (largeur, alignement). */
  style?: React.CSSProperties;
  /**
   * Nature de la donnée. `nombre` : aligné à droite, chiffres à chasse fixe,
   * filtre min/max. `date` : filtre du/au sur la valeur ISO, et le `rendu` DOIT
   * passer par `fmtDate` (contrôle statique). `liste` : filtre à cases sur les
   * valeurs distinctes. `texte` (défaut) : filtre « contient ».
   */
  type?: TypeColonne;
  /** Colonne de l'EXPORT de référence (PROMPT-TABLEAUX §1). */
  export?: boolean;
  /** Libellé TEXTE (export, menu des colonnes, puces) quand `libelle` n'est pas une chaîne. */
  titre?: string;
  /** Valeur exportée, quand `String(valeur)` ne convient pas (une date : passer `fmtDate`). */
  exporter?: (ligne: L) => string;
  /** Imposée par le paramétrage du laboratoire : jamais masquable. */
  obligatoire?: boolean;
  /** `false` : aucun filtre de colonne, même au niveau standard. */
  filtrable?: boolean;
  /**
   * Épinglée PAR LE REGISTRE (la colonne d'identifiant d'un gros registre, L3) :
   * toujours à gauche, avant les épinglées de la personne, et personne ne la
   * désépingle. L'identifiant est ce qu'on lit pendant qu'on défile à droite.
   */
  epinglee?: boolean;
  /**
   * MASQUÉE PAR DÉFAUT (Ines, 20/10/2026) : la colonne existe, se choisit dans le menu « Colonnes », mais
   * n'est pas montrée à qui n'a jamais réglé ce tableau. La réafficher se garde (une ligne en base reste).
   */
  masqueeParDefaut?: boolean;
  /** Le libellé d'une VALEUR brute (filtre « liste », puces, export) : un rang de gravité → « critique ». */
  libelleValeur?: (valeur: string) => string;
  /** La DÉFINITION de la colonne, en infobulle d'en-tête (« Jours où la personne est au planning, hors garde »). */
  aide?: string;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LA LIGNE S'OUVRE AU CLIC — et le bouton « Modifier » disparaît de la colonne
 * d'actions (Ines, 26/08/2026 : « un clic sur la ligne d'intérêt suffit »).
 *
 * Une `<tr>` n'est NI FOCALISABLE NI SENSIBLE À ENTRÉE : un simple `onClick`
 * rend le registre entier inaccessible au clavier. Il faut cinq attributs
 * solidaires, et cinq attributs recopiés onze fois divergent. Ils vivent ici.
 *
 * ⚠️ COROLLAIRE — les boutons qui RESTENT dans la ligne doivent arrêter la
 * propagation, sinon leur clic ouvre AUSSI la fiche : `SansOuvrir`.
 * ═══════════════════════════════════════════════════════════════════════════ */
/** Un clic que le NAVIGATEUR doit traiter lui-même : nouvelle fenêtre, nouvel onglet, téléchargement. */
export function clicDuNavigateur(e: React.MouseEvent): boolean {
  return e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0;
}

export function ligneOuvrable(
  onOuvrir: () => void,
  /** Ce que l'on ouvre, nommé : le seul indice qu'a un lecteur d'écran. « Ouvrir NC-2026-014 ». */
  titre: string,
  style?: React.CSSProperties,
  /** La ligne porte une `<CelluleLien>` : c'est le LIEN qui est la commande, la rangée cesse d'être focalisable. */
  avecLien?: boolean,
): React.HTMLAttributes<HTMLElement> {
  return {
    className: 'ged-row',
    /* Ctrl+clic, ⌘+clic, Maj+clic et clic molette veulent dire « ouvre ailleurs » :
       convention du navigateur, pas de l'application (P0.3, 27/08/2026). */
    onClick: (e) => { if (!clicDuNavigateur(e)) onOuvrir(); },
    title: titre,
    ...(avecLien ? {} : {
      tabIndex: 0,
      role: 'button',
      'aria-label': titre,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOuvrir(); }
      },
    }),
    style: { cursor: 'pointer', ...style },
  };
}

/**
 * UNE LIGNE DE LISTE QUI EST UN VRAI LIEN (listes de cartes). Le clavier y
 * trouve Entrée nativement, le navigateur ses gestes, et le clic ordinaire ouvre
 * la fiche en surcouche parce qu'on annule sa navigation par défaut.
 */
export function LigneLien({ href, onOuvrir, className = 'ged-row', style, children, title }: {
  href: string; onOuvrir: () => void; className?: string; style?: React.CSSProperties;
  children: React.ReactNode; title?: string;
}) {
  return (
    <a href={href} className={className} title={title}
      onClick={(e) => { if (clicDuNavigateur(e)) return; e.preventDefault(); onOuvrir(); }}
      style={{ display: 'block', textDecoration: 'none', color: 'inherit', cursor: 'pointer', ...style }}>
      {children}
    </a>
  );
}

/**
 * LA PREMIÈRE CELLULE D'UNE LIGNE OUVRABLE — un vrai lien (P0.3). Pas de
 * `stopPropagation` : le clic doit CONTINUER jusqu'à la rangée, qui porte
 * l'ouverture. C'est l'inverse de `SansOuvrir`.
 */
export function CelluleLien({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href}
      onClick={(e) => { if (!clicDuNavigateur(e)) e.preventDefault(); }}
      style={{ color: 'inherit', textDecoration: 'none', display: 'block' }}>
      {children}
    </a>
  );
}

/** Enveloppe une colonne d'ACTIONS dans une ligne ouvrable : le clic sur un
 *  bouton ne doit pas ouvrir la fiche par-dessus le geste demandé. */
export function SansOuvrir({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}
      onClick={(e) => e.stopPropagation()}
      /* `keydown` aussi : la ligne écoute Entrée et Espace. */
      onKeyDown={(e) => e.stopPropagation()}>
      {children}
    </span>
  );
}

/* Tableau hors boîte, sur écran étroit : il défile de côté chez lui.
   `overflow-x: auto` fait de la boîte un conteneur de défilement ; l'en-tête
   s'y réfère alors, et le décalage de la barre (`--haut-entete`, mesuré une
   fois au montage) le pousserait au milieu de la première ligne après une
   rotation de l'écran : on le remet à zéro. */
const FEUILLE_TABLEAU = `
@media (max-width: 980px) {
  .bio-tableau-boite { overflow-x: auto !important; overscroll-behavior-x: contain; -webkit-overflow-scrolling: touch; max-width: 100%; }
  .bio-tableau-boite > table { --haut-entete: 0px !important; }
}
`;

/* Hauteur de la barre de connexion, `sticky` en tête de page (Shell : 44 + 2×8). */
const HAUT_BARRE = 60;

/* ═══════════════════════════════════════════════════════════════════════════
 * OÙ L'EN-TÊTE COLLE — mesuré, et non supposé (correctif du 27/08/2026).
 * `sticky` se réfère au premier ancêtre qui défile : on le CHERCHE, comme le
 * navigateur, au lieu de poser un nombre qui n'est juste que dans un cas.
 * Écrit dans une propriété CSS depuis un `ref` de rappel : rien à re-rendre.
 * ═══════════════════════════════════════════════════════════════════════════ */
export function enTeteCollant(boite: HTMLElement | null) {
  if (!boite || typeof window === 'undefined') return;
  let n: HTMLElement | null = boite.parentElement;
  let dansUneBoite = false;
  while (n && n !== document.body) {
    const o = getComputedStyle(n);
    if (o.overflow !== 'visible' || o.overflowX !== 'visible' || o.overflowY !== 'visible') { dansUneBoite = true; break; }
    n = n.parentElement;
  }
  boite.style.setProperty('--haut-entete', `${dansUneBoite ? 0 : HAUT_BARRE}px`);

  /* ⚠️ UN TABLEAU TROP LARGE SE COUPE EN SILENCE — on le DIT en développement :
     la carte porte `overflow: hidden` pour son arrondi, la dernière colonne
     disparaît sans signal. Mesuré au rendu suivant (au `ref`, tout vaut zéro). */
  if (process.env.NODE_ENV !== 'production') {
    requestAnimationFrame(() => {
      const cadre = boite.parentElement;
      if (!cadre || dansUneBoite) return;
      const deborde = boite.scrollWidth - cadre.clientWidth;
      if (deborde > 2) {
        console.warn(
          `[Tableau] Ce tableau dépasse son cadre de ${Math.round(deborde)} px : sa dernière colonne est `
          + "tronquée sans le dire. Une colonne longue doit porter `whiteSpace: 'normal'` pour céder la place.",
          boite,
        );
      }
    });
  }
}

/* ───────────────────────────── Densité (É-3) ─────────────────────────────── */

/** Hauteur de ligne ESTIMÉE par densité, pour le virtualiseur seulement (il mesure ensuite). */
const HAUTEUR_ESTIMEE: Record<Densite, number> = { normale: 44, compacte: 32 };

function jetons(densite: Densite) {
  const base = densite === 'compacte' ? '--tableau-ligne-compacte' : '--tableau-ligne';
  return {
    hauteur: `var(${base})`,
    police: `var(${base}-police)`,
    padY: `var(${base}-padding-y)`,
    padX: `var(${base}-padding-x)`,
    entete: densite === 'compacte' ? 'var(--tableau-entete-compacte-police)' : 'var(--tableau-entete-police)',
  };
}

/* ───────────────────────────── Petits composants d'état ──────────────────── */

function SkeletonTableau({ rows = 5, hauteur }: { rows?: number; hauteur: string }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Chargement en cours…</span>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ height: hauteur, borderTop: i ? '1px solid var(--border-subtle, var(--border-default))' : 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '0 18px' }}>
          <span className="ged-skel-bar" style={{ width: 96, height: 10, borderRadius: 'var(--radius-xs)', background: 'var(--surface-sunken)' }} />
          <span className="ged-skel-bar" style={{ flex: 1, maxWidth: 320, height: 10, borderRadius: 'var(--radius-xs)', background: 'var(--surface-sunken)' }} />
          <span className="ged-skel-bar" style={{ width: 72, height: 10, borderRadius: 'var(--radius-xs)', background: 'var(--surface-sunken)' }} />
        </div>
      ))}
    </div>
  );
}

function ErreurTableau({ message, onReessayer }: { message: string; onReessayer?: () => void }) {
  return (
    <div role="alert" style={{ padding: '26px 18px', textAlign: 'center' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--red-600)' }}>
        <Icon name="alert" size={16} />{message}
      </div>
      {onReessayer && (
        <div style={{ marginTop: 12 }}>
          <Bouton taille="sm" onClick={onReessayer} icone="refresh">Réessayer</Bouton>
        </div>
      )}
    </div>
  );
}

function VideTableau({ titre, indice, action }: { titre: string; indice?: string; action?: React.ReactNode }) {
  return (
    <div style={{ padding: '30px 18px', textAlign: 'center' }}>
      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-body)' }}>{titre}</div>
      {indice && <div style={{ fontSize: 'var(--tableau-ligne-police)', color: 'var(--text-muted)', marginTop: 5 }}>{indice}</div>}
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

/** La barre de pagination. Ne rend RIEN quand il n'y a qu'une page. */
function PaginationTableau({ page, pages, debut, fin, total, onPage, quoi = 'éléments' }: {
  page: number; pages: number; debut: number; fin: number; total: number; onPage: (p: number) => void; quoi?: string;
}) {
  if (pages <= 1) return null;
  const aller = (p: number) => {
    onPage(Math.min(pages, Math.max(1, p)));
    // Changer de page sans remonter laisse la personne au MILIEU de la nouvelle.
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  return (
    <nav aria-label="Pagination" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
      {/* `aria-live` : au clavier, changer de page ne déplace pas le focus. */}
      <span aria-live="polite" style={{ fontSize: 'var(--tableau-ligne-police)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
        {debut}–{fin} sur {total} {quoi}
      </span>
      <span style={{ flex: 1 }} />
      <Bouton taille="sm" icone="chevronLeft" aria-label="Page précédente" disabled={page <= 1} onClick={() => aller(page - 1)} />
      <span style={{ fontSize: 'var(--tableau-ligne-police)', color: 'var(--text-body)', fontVariantNumeric: 'tabular-nums' }}>
        page {page} / {pages}
      </span>
      <Bouton taille="sm" icone="chevronRight" aria-label="Page suivante" disabled={page >= pages} onClick={() => aller(page + 1)} />
    </nav>
  );
}

/** jj/mm/aaaa depuis un jour ISO — pour les puces de filtre seulement (le `rendu` des cellules passe par `fmtDate`). */
const jourLisible = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const champ: React.CSSProperties = {
  height: 30, border: '1.5px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
  padding: '0 8px', fontSize: 'var(--tableau-ligne-police)', color: 'var(--text-strong)',
  fontFamily: 'var(--font-sans)', backgroundColor: 'var(--surface-card)', width: '100%',
};

const panneau: React.CSSProperties = {
  position: 'absolute', zIndex: 20, marginTop: 4, minWidth: 220, maxWidth: 320,
  background: 'var(--surface-card)', border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md, var(--shadow-sm))', padding: 10,
  fontSize: 'var(--tableau-ligne-police)', color: 'var(--text-body)', textTransform: 'none',
  letterSpacing: 0, fontWeight: 400, whiteSpace: 'normal', cursor: 'default', textAlign: 'start',
};

/* ───────────────────────────── Préférences (L1 : en base, cache local) ───── */

/** Le cache, synchrone, pour le premier rendu : le serveur prévaut ensuite (`chargerPref`). */
function prefInitiale(id: string, cles: readonly string[], masqueesDefaut: readonly string[]): PrefTableau {
  if (typeof window === 'undefined') return masqueesDefaut.length ? { ...PREF_VIDE, masquees: [...masqueesDefaut] } : PREF_VIDE;
  const p = prefDuCache(cacheNavigateur, id, cles);
  return masqueesDefaut.length && !ecartsPref(cles, p) ? { ...p, masquees: [...masqueesDefaut] } : p;
}

/** Le réglage est celui d'origine : rien d'autre que les colonnes masquées par défaut. */
function auDefaut(cles: readonly string[], pref: PrefTableau, masqueesDefaut: readonly string[]): boolean {
  if (!masqueesDefaut.length) return false;
  const e = ecartsPref(cles, pref);
  return !!e && Object.keys(e).length === 1 && [...(e.masquees ?? [])].sort().join('|') === [...masqueesDefaut].sort().join('|');
}

/** Cache ET serveur, écarts seulement. Une panne ne retire rien : le cache garde le réglage. */
function ecrirePref(id: string, cles: readonly string[], pref: PrefTableau, masqueesDefaut: readonly string[] = []): void {
  ecrireCache(cacheNavigateur, id, cles, pref);
  const ecarts = ecartsPref(cles, pref);
  const depot = depotPrefs();
  /* UNE COLONNE MASQUÉE PAR DÉFAUT, RÉAFFICHÉE : « aucune masquée » n'est plus le défaut. La ligne doit rester —
     on écrit l'ordre en clair, sans la liste des colonnes, pour que le serveur ne l'efface pas comme « au défaut ». */
  if (masqueesDefaut.length && !ecarts?.masquees?.length) {
    depot.ecrire(id, null, { ...(ecarts ?? {}), ordre: colonnesOrdonnees(cles, pref) })
      .catch(() => { /* panne réseau : le cache garde le réglage */ });
    return;
  }
  (ecarts ? depot.ecrire(id, cles, ecarts) : depot.effacer(id))
    .catch(() => { /* panne réseau : le cache garde le réglage, la prochaine écriture retentera */ });
}

/* La migration des clés locales d'avant L1 se joue UNE fois par chargement de
   l'application, au premier tableau monté — pas une fois par tableau. */
let migrationLancee = false;
function migrerUneFois(): void {
  if (migrationLancee || typeof window === 'undefined') return;
  migrationLancee = true;
  void migrerPrefsLocales({ cache: cacheNavigateur, serveur: depotPrefs() });
}

/* ═══════════════════════════════════════════════════════════════════════════ */

export interface TableauProps<L> {
  /** Identifiant STABLE de l'écran — clé des préférences. */
  id: string;
  colonnes: ColonneTriable<L>[];
  lignes: L[];
  cleLigne: (ligne: L) => string;
  /** Props de la rangée (onClick, style) — la ligne reste cliquable. */
  ligneProps?: (ligne: L) => React.HTMLAttributes<HTMLTableRowElement>;
  /**
   * Hauteur au-delà de laquelle la liste défile DANS sa propre boîte.
   * ⚠️ SANS VALEUR PAR DÉFAUT (27/08/2026, P1) : un tableau défile avec la page,
   * et l'en-tête `sticky` colle à la PAGE. À réserver aux hauteurs bornées par choix.
   */
  hauteurMax?: number;
  /**
   * Le tableau est plus large que sa colonne et doit défiler HORIZONTALEMENT.
   * ⚠️ Un conteneur contraint sur un axe l'est sur les deux : l'en-tête colle
   * alors à la boîte, pas à la page. À ne poser que sur les tableaux réellement larges.
   */
  defilementHorizontal?: boolean;
  /**
   * Le tableau tient dans la largeur de l'écran au lieu de défiler de côté
   * (Ines, 19/10/2026). `true` : les colonnes de TEXTE et les en-têtes passent
   * à la ligne, le navigateur répartit la place selon le contenu ; nombres,
   * dates et actions restent sur une ligne. `'fixe'` : parts égales
   * (`table-layout: fixed`), tout se coupe — le récapitulatif de paie.
   */
  ajusterLargeur?: boolean | 'fixe';
  /** Pagine le tableau LUI-MÊME : trier tout, puis découper. */
  parPage?: number;
  /** Ce que la barre de pagination compte, au pluriel — « actes », « lignes ». */
  quoi?: string;
  /** LE DÉTAIL DÉPLIÉ d'une ligne, dans une rangée qui suit la sienne. `null` = rien. */
  detail?: (ligne: L) => React.ReactNode;

  /* ── L0 ─────────────────────────────────────────────────────────────────── */
  /** Active les fonctions du socle (AUDIT-TABLES §5). Absent = comportement historique. */
  niveau?: Niveau;
  /** `compacte` : réservée aux grilles denses (planning, présences). */
  densite?: Densite;
  /** Boîte de recherche globale (`true`, ou le texte d'invite). */
  recherche?: boolean | string;
  /** États intégrés (É-13) : squelette, puis erreur + « Réessayer », puis état vide. */
  chargement?: boolean;
  erreur?: string | null;
  onReessayer?: () => void;
  /** L'état vide, quand les états sont intégrés. Défaut : « Aucune ligne ». */
  vide?: { titre: string; indice?: string; action?: React.ReactNode };
  /** La DERNIÈRE colonne, « Actions » — rendre `<ActionsLigne …/>`. `SansOuvrir` est posé si la ligne s'ouvre. */
  actions?: (ligne: L) => React.ReactNode;
  /** Export de référence (colonnes `export: true`, lignes filtrées). `filtres` : ceux de l'écran, en clair.
   *  `colonnes` (L10) : les colonnes à exporter quand elles diffèrent de celles AFFICHÉES —
   *  l'export de référence sort TOUTES les colonnes du registre, pas la sélection du jour. */
  export?: { registre: string; libelleRegistre: string; auteur: string; filtres?: string[]; colonnes?: ColonneTriable<L>[] };
  /** Actions par lot (niveau avancé). Jamais une suppression. */
  actionsLot?: ActionLot<L>[];
  /** Colonnes rendues obligatoires par le paramétrage du laboratoire (en plus de `obligatoire` sur la colonne). */
  colonnesObligatoires?: string[];
  /**
   * LE PIED (totaux, comptes) : une cellule par colonne visible, calculée sur les
   * lignes FILTRÉES — un total qui ignorerait le filtre mentirait. Rendu dans un
   * `<tfoot>`, donc jamais trié ni virtualisé avec les lignes.
   */
  pied?: (colonne: ColonneTriable<L>, lignes: L[]) => React.ReactNode;
}

/**
 * ⚠️ CHOISIR CE COMPOSANT, C'EST CHOISIR UN PATRON D'OUVERTURE DE FICHE
 * (arbitrage SYS-5 du 27/08/2026) : un registre rendu ici occupe toute la
 * largeur, sa fiche s'ouvre en FENÊTRE. Une liste étroite (`ged-colonnes-defilantes`)
 * ouvre À CÔTÉ. Ne pas mélanger dans un même écran (`qualite-patrons-fiche`).
 */
export function Tableau<L>({
  id, colonnes, lignes, cleLigne, ligneProps, hauteurMax, defilementHorizontal, ajusterLargeur, parPage, quoi, detail,
  niveau, densite = 'normale', recherche: rechercheProp, chargement, erreur, onReessayer, vide, actions,
  export: exportConf, actionsLot, colonnesObligatoires, pied,
}: TableauProps<L>) {
  const cap = capacites(niveau);
  const j = jetons(densite);
  const cles = React.useMemo(() => colonnes.map((c) => c.cle), [colonnes]);
  /* Colonnes imposées par le PARAMÉTRAGE du laboratoire, et le droit de les régler (RQ). */
  const [obligatoiresServeur, setObligatoiresServeur] = React.useState<string[]>([]);
  const [peutRegler, setPeutRegler] = React.useState(false);
  const obligatoires = React.useMemo(
    () => [...colonnes.filter((c) => c.obligatoire).map((c) => c.cle), ...(colonnesObligatoires ?? []), ...obligatoiresServeur],
    [colonnes, colonnesObligatoires, obligatoiresServeur],
  );
  const parCle = React.useMemo(() => new Map(colonnes.map((c) => [c.cle, c])), [colonnes]);
  const titreDe = (c: ColonneTriable<L>) => c.titre ?? (typeof c.libelle === 'string' ? c.libelle : c.cle);

  /* ── Préférences (ordre, masquées, épinglées, largeurs, tri) ────────────── */
  const masqueesDefaut = React.useMemo(() => colonnes.filter((c) => c.masqueeParDefaut && !c.obligatoire).map((c) => c.cle), [colonnes]);
  const masqueesDefautTexte = masqueesDefaut.join('|');
  const [pref, setPref] = React.useState<PrefTableau>(() => prefInitiale(id, cles, masqueesDefaut));
  const prefRef = React.useRef(pref);
  prefRef.current = pref;
  const changerPref = (suivante: PrefTableau) => { setPref(suivante); ecrirePref(id, cles, suivante, masqueesDefaut); };
  const [tri, setTri] = React.useState<SortingState>([]);
  const clesTexte = cles.join('|');
  React.useEffect(() => {
    /* Le serveur fait foi : la ligne (personne, tableau) remplace le cache dès
       qu'elle arrive. Sans réponse, on garde le cache — et aucun message. */
    let actif = true;
    const liste = clesTexte ? clesTexte.split('|') : [];
    migrerUneFois();
    chargerPref({ id, cles: liste, cache: cacheNavigateur, serveur: depotPrefs() }).then((r) => {
      if (!actif) return;
      /* Jamais réglé (ni ligne, ni cache) : les colonnes masquées par défaut le sont. */
      const defaut = masqueesDefautTexte ? masqueesDefautTexte.split('|') : [];
      const pref = defaut.length && !r.ligne && !ecartsPref(liste, r.pref) ? { ...r.pref, masquees: defaut } : r.pref;
      setPref(pref);
      setObligatoiresServeur(r.obligatoires);
      setPeutRegler(r.peutRegler);
      setTri(r.pref.tri ? [{ id: r.pref.tri.cle, desc: r.pref.tri.sens === -1 }] : []);
    });
    return () => { actif = false; };
  }, [id, clesTexte, masqueesDefautTexte]);

  const epingleesFixes = React.useMemo(() => colonnes.filter((c) => c.epinglee).map((c) => c.cle), [colonnes]);
  const visiblesCles = React.useMemo(
    () => colonnesVisibles(cles, { ...pref, epinglees: cap.epingler ? pref.epinglees : [] }, obligatoires, epingleesFixes),
    [cles, pref, obligatoires, cap.epingler, epingleesFixes],
  );
  const visibles = React.useMemo(
    () => visiblesCles.map((k) => parCle.get(k)).filter(Boolean) as ColonneTriable<L>[],
    [visiblesCles, parCle],
  );
  const epinglees = React.useMemo(
    () => visiblesCles.filter((k) => epingleesFixes.includes(k) || (cap.epingler && pref.epinglees.includes(k))),
    [cap.epingler, pref.epinglees, visiblesCles, epingleesFixes],
  );

  /* ── Filtres et recherche ──────────────────────────────────────────────── */
  const [filtres, setFiltres] = React.useState<Record<string, FiltreColonne>>({});
  const [recherche, setRecherche] = React.useState('');
  const [menu, setMenu] = React.useState<'colonnes' | `filtre:${string}` | null>(null);
  const valeurs = React.useMemo(() => {
    const o: Record<string, (l: L) => string | number | null | undefined> = {};
    for (const c of colonnes) if (c.valeur) o[c.cle] = c.valeur;
    return o;
  }, [colonnes]);
  const filtresActifs = React.useMemo(
    () => Object.fromEntries(Object.entries(filtres).filter(([k, f]) => filtreActif(f) && parCle.has(k))),
    [filtres, parCle],
  );
  const nbFiltres = Object.keys(filtresActifs).length + (recherche.trim() ? 1 : 0);
  const lignesFiltrees = React.useMemo(
    () => (nbFiltres ? lignes.filter((l) => passeFiltres(l, valeurs, filtresActifs, recherche)) : lignes),
    [lignes, valeurs, filtresActifs, recherche, nbFiltres],
  );

  /* ── TanStack : tri, pagination, sélection ─────────────────────────────── */
  const [selection, setSelection] = React.useState<RowSelectionState>({});
  const [pagination, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize: parPage ?? Number.MAX_SAFE_INTEGER });
  const avecSelection = cap.lot && !!actionsLot?.length;

  const defs = React.useMemo<ColumnDef<L>[]>(() => colonnes.map((c) => ({
    id: c.cle,
    /* Les VIDES deviennent `undefined` : TanStack les range alors en QUEUE quel
       que soit le sens (`sortUndefined: 'last'`). Trier par échéance doit
       montrer les échéances, pas un bloc de tirets en tête. */
    accessorFn: (l: L) => { const v = c.valeur?.(l); return estVide(v) ? undefined : v; },
    enableSorting: !!c.valeur,
    // Croissant → décroissant → l'ordre métier d'origine, même pour les nombres.
    sortDescFirst: false,
    sortUndefined: 'last',
    sortingFn: (a, b, colId) => comparerValeurs(a.getValue<string | number>(colId), b.getValue<string | number>(colId)),
  })), [colonnes]);

  const table = useReactTable<L>({
    data: lignesFiltrees,
    columns: defs,
    getRowId: (l) => cleLigne(l),
    state: { sorting: tri, rowSelection: selection, pagination },
    onSortingChange: (u) => {
      const suivant = typeof u === 'function' ? u(tri) : u;
      setTri(suivant);
      // Le tri se retient avec le reste de la préférence (L1).
      const t = suivant[0];
      changerPref({ ...prefRef.current, tri: t ? { cle: t.id, sens: t.desc ? -1 : 1 } : null });
    },
    onRowSelectionChange: setSelection,
    onPaginationChange: setPagination,
    enableSortingRemoval: true,
    enableRowSelection: avecSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // Filtrer depuis la page 7 laisse la page 7 d'une liste qui n'en a plus que 2 : on revient page 1.
    autoResetPageIndex: true,
  });
  const rangees = table.getRowModel().rows;
  const total = lignesFiltrees.length;
  const nbPages = parPage ? Math.max(1, Math.ceil(total / parPage)) : 1;
  const triCourant = tri[0];

  /* ── Virtualisation (É-14) ─────────────────────────────────────────────── */
  const enBoite = hauteurMax !== undefined || !!defilementHorizontal;
  /* ⚠️ Qui DÉFILE VERTICALEMENT ? Seule une hauteur bornée fait défiler la
     boîte ; `defilementHorizontal` seul la fait défiler de côté, et c'est la
     PAGE qui descend. Virtualiser sur la boîte dans ce cas rend toutes les
     lignes (sa hauteur est celle de son contenu) — constaté sur les 330
     paramètres du catalogue le 10/10/2026. */
  const defileDansBoite = hauteurMax !== undefined;
  const virtualiser = doitVirtualiser(total, parPage);
  const boiteRef = React.useRef<HTMLDivElement>(null);
  const corpsRef = React.useRef<HTMLTableSectionElement>(null);
  const margeRef = React.useRef(0);
  const estimation = HAUTEUR_ESTIMEE[densite];
  const mesurer = React.useCallback((el: Element) => mesurerLigneEtDetail(el as unknown as RangeeMesurable), []);
  const vBoite = useVirtualizer({
    count: rangees.length, getScrollElement: () => boiteRef.current, estimateSize: () => estimation,
    overscan: 8, enabled: virtualiser && defileDansBoite, measureElement: mesurer,
  });
  const vFenetre = useWindowVirtualizer({
    count: rangees.length, estimateSize: () => estimation, overscan: 8,
    enabled: virtualiser && !defileDansBoite, measureElement: mesurer, scrollMargin: margeRef.current,
  });
  const virt = defileDansBoite ? vBoite : vFenetre;
  React.useLayoutEffect(() => {
    /* L'offset du corps dans la PAGE, pour le virtualiseur de fenêtre. Écrit
       dans un `ref`, pas dans un état : le virtualiseur relit ses options à
       chaque défilement, et un `setState` ici relancerait un rendu en cascade. */
    if (!virtualiser || defileDansBoite || !corpsRef.current) return;
    margeRef.current = corpsRef.current.getBoundingClientRect().top + window.scrollY;
  });
  const items = virtualiser ? virt.getVirtualItems() : null;
  const hautAvant = items && items.length ? items[0]!.start - (virt.options.scrollMargin ?? 0) : 0;
  const hautApres = items && items.length ? virt.getTotalSize() - (items[items.length - 1]!.end - (virt.options.scrollMargin ?? 0)) : 0;

  /* ── Largeurs : réglage à la souris, mesure pour l'épinglage ───────────── */
  const glisse = React.useRef<{ cle: string; x0: number; l0: number } | null>(null);
  React.useEffect(() => {
    const bouge = (e: MouseEvent) => {
      const g = glisse.current;
      if (!g) return;
      e.preventDefault();
      const l = bornerLargeur(g.l0 + (e.clientX - g.x0));
      setPref((p) => (p.largeurs[g.cle] === l ? p : { ...p, largeurs: { ...p.largeurs, [g.cle]: l } }));
    };
    const lache = () => {
      if (!glisse.current) return;
      glisse.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // On n'écrit qu'au RELÂCHEMENT : enregistrer à chaque pixel remplirait le stockage de deux cents écritures.
      ecrirePref(id, cles, prefRef.current, masqueesDefaut);
    };
    document.addEventListener('mousemove', bouge);
    document.addEventListener('mouseup', lache);
    return () => { document.removeEventListener('mousemove', bouge); document.removeEventListener('mouseup', lache); };
  }, [id, cles]);

  const [largeursMesurees, setLargeursMesurees] = React.useState<Record<string, number>>({});
  const entetes = React.useRef(new Map<string, HTMLTableCellElement>());
  const observateur = React.useRef<ResizeObserver | null>(null);
  React.useEffect(() => {
    if (!epinglees.length || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entrees) => {
      setLargeursMesurees((m) => {
        let suivant = m; let change = false;
        for (const e of entrees) {
          const k = (e.target as HTMLElement).dataset.cle;
          const w = Math.round((e.target as HTMLElement).offsetWidth);
          if (k && suivant[k] !== w) { if (!change) { suivant = { ...m }; change = true; } suivant[k] = w; }
        }
        return change ? suivant : m;
      });
    });
    observateur.current = ro;
    entetes.current.forEach((el) => ro.observe(el));
    return () => { ro.disconnect(); observateur.current = null; };
  }, [epinglees.length, visiblesCles]);
  const refEntete = (cle: string) => (el: HTMLTableCellElement | null) => {
    if (el) { entetes.current.set(cle, el); observateur.current?.observe(el); } else entetes.current.delete(cle);
  };
  const LARGEUR_SELECTION = 36;
  const decalages = React.useMemo(
    () => decalagesEpingles(visiblesCles, epinglees, { ...largeursMesurees, ...pref.largeurs }, 150, avecSelection ? LARGEUR_SELECTION : 0),
    [visiblesCles, epinglees, largeursMesurees, pref.largeurs, avecSelection],
  );
  const derniereEpinglee = epinglees.length ? visiblesCles[epinglees.length - 1] : null;
  /* La case de sélection s'épingle avec la première colonne épinglée : sans cela
     elle partirait à gauche au défilement pendant que l'identifiant reste. */
  const styleSelection = (th: boolean): React.CSSProperties => (epinglees.length ? {
    position: 'sticky', insetInlineStart: 0, zIndex: th ? 4 : 1, background: 'var(--surface-card, #fff)',
  } : {});

  /* ── Glisser-déposer des colonnes ──────────────────────────────────────── */
  const [portee, setPortee] = React.useState<string | null>(null);
  const [survolee, setSurvolee] = React.useState<string | null>(null);

  /* ── Menus : fermeture au clic dehors et à Échap ───────────────────────── */
  const racine = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!menu) return;
    const dehors = (e: MouseEvent) => {
      const cible = e.target as Node;
      const ouverts = racine.current?.querySelectorAll('[data-panneau]') ?? [];
      for (const p of Array.from(ouverts)) if (p.contains(cible)) return;
      if ((cible as HTMLElement).closest?.('[data-ouvre-panneau]')) return;
      setMenu(null);
    };
    const echap = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null); };
    document.addEventListener('mousedown', dehors);
    document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', echap); };
  }, [menu]);

  /* ── Actions par lot ───────────────────────────────────────────────────── */
  const [confirmation, setConfirmation] = React.useState<{ action: ActionLot<L>; lignes: L[] } | null>(null);
  const [bilan, setBilan] = React.useState<{ action: ActionLot<L>; bilan: BilanLot } | null>(null);
  const [lotEnCours, setLotEnCours] = React.useState(false);
  /* L10 : le motif commun au lot, quand l'action en exige un. */
  const [motifLot, setMotifLot] = React.useState('');
  const selectionnees = avecSelection ? table.getSelectedRowModel().rows.map((r) => r.original) : [];
  const lancerLot = async () => {
    if (!confirmation || !motifLotValide(confirmation.action, motifLot)) return;
    setLotEnCours(true);
    try {
      const b = await executerParLot(confirmation.lignes, confirmation.action, cleLigne, undefined, motifLot);
      setBilan({ action: confirmation.action, bilan: b });
      table.resetRowSelection();
      /* L10 : l'appelant recharge sa liste UNE fois le lot passé, pas ligne par ligne. */
      await confirmation.action.apresLot?.(b);
    } finally {
      setLotEnCours(false);
      setConfirmation(null);
    }
  };

  /* ── Export de référence ───────────────────────────────────────────────── */
  const colonnesExport = colonnesExportables(exportConf?.colonnes ?? colonnes);
  const exporter = () => {
    if (!exportConf) return;
    const triees = table.getPrePaginationRowModel().rows.map((r) => r.original);
    telechargerExport<L>({
      registre: exportConf.registre,
      libelleRegistre: exportConf.libelleRegistre,
      auteur: exportConf.auteur,
      lignes: triees,
      colonnes: colonnesExport.map((c) => ({
        libelle: titreDe(c),
        valeur: c.exporter ?? ((l: L) => { const v = c.valeur?.(l); return estVide(v) ? '' : String(v); }),
      })),
      filtres: [
        ...(exportConf.filtres ?? []),
        ...Object.entries(filtresActifs).map(([k, f]) => decrireFiltre(titreDe(parCle.get(k)!), f, jourLisible, parCle.get(k)!.libelleValeur)),
        ...(recherche.trim() ? [`Recherche « ${recherche.trim()} »`] : []),
      ],
    });
  };

  /* ── Ce que la barre d'outils doit montrer ─────────────────────────────── */
  const montrerRecherche = cap.recherche && !!rechercheProp;
  const montrerColonnes = cap.masquer;
  const nbMasquees = pref.masquees.filter((k) => cles.includes(k) && !obligatoires.includes(k)).length;
  const montrerExport = cap.export && !!exportConf;
  const montrerFiltres = cap.filtres;
  const barre = montrerRecherche || montrerColonnes || montrerExport || nbFiltres > 0 || selectionnees.length > 0 || !!bilan;

  const etatsIntegres = chargement !== undefined || erreur !== undefined || vide !== undefined;
  const colSpan = colSpanDetail(visibles.length, { selection: avecSelection, actions: !!actions });

  /* ═════════════════════════════ Styles de cellule ════════════════════════ */
  const enTeteBase: React.CSSProperties = {
    padding: `calc(${j.padY} + 2px) ${j.padX}`, borderBottom: '1px solid var(--border-default)',
    textAlign: 'start', fontSize: j.entete, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '.03em', color: 'var(--text-muted)', whiteSpace: 'nowrap',
    background: 'var(--surface-card, #fff)', position: 'sticky', zIndex: 2,
  };
  /* ⚠️ `height` SUR UNE CELLULE AGIT COMME UN MINIMUM (P3a) : le plancher absorbe
     les variantes basses, les lignes réellement hautes restent hautes. Par
     défaut une cellule ne se coupe pas (Inès, 28/08/2026) : les colonnes
     longues le disent avec `whiteSpace: 'normal'`. */
  const celluleBase: React.CSSProperties = {
    padding: `${j.padY} ${j.padX}`, borderBottom: '1px solid var(--border-subtle, var(--border-default))',
    textAlign: 'start', verticalAlign: 'top', fontSize: j.police, height: j.hauteur, whiteSpace: 'nowrap',
  };
  const nombre: React.CSSProperties = { textAlign: 'end', fontVariantNumeric: 'tabular-nums' };
  /* AJUSTER À L'ÉCRAN. `'fixe'` impose sa coupe à toutes les colonnes ; `true`
     ne coupe que le texte, et AVANT le `style` de la colonne : une colonne qui
     demande `nowrap` le garde. `break-word` et non `anywhere` : un code ne se
     casse que s'il ne tient vraiment pas, il n'est pas compté pour rétrécir. */
  const fixe = ajusterLargeur === 'fixe';
  const souple = ajusterLargeur === true;
  const celluleSouple = (c: ColonneTriable<L>): React.CSSProperties =>
    (!souple || c.type === 'nombre' || c.type === 'date' ? {} : { whiteSpace: 'normal', overflowWrap: 'break-word' });
  const styleEpingle = (cle: string, th: boolean): React.CSSProperties => (decalages[cle] === undefined ? {} : {
    position: 'sticky', insetInlineStart: decalages[cle], zIndex: th ? 4 : 1,
    background: 'var(--surface-card, #fff)',
    ...(cle === derniereEpinglee ? { boxShadow: 'inset -1px 0 0 var(--border-default)' } : {}),
  });

  /* ═════════════════════════════ Rendu ════════════════════════════════════ */
  const corps = (() => {
    if (chargement) return <SkeletonTableau hauteur={j.hauteur} />;
    if (erreur) return <ErreurTableau message={erreur} onReessayer={onReessayer} />;
    if (etatsIntegres && lignes.length === 0) return <VideTableau {...(vide ?? { titre: 'Aucune ligne' })} />;

    const rendreLigne = (l: L, index: number) => {
      const deplie = detail ? detail(l) : null;
      const props = ligneProps ? ligneProps(l) : {};
      /* ── LE CLAVIER, UNE FOIS POUR TOUTES : on ne complète que ce qui manque, et
         seulement sur une ligne qui répond au clic. */
      const cliquable = typeof props.onClick === 'function';
      const auClavier: React.HTMLAttributes<HTMLTableRowElement> = !cliquable ? {} : {
        tabIndex: props.tabIndex ?? 0,
        role: props.role ?? 'button',
        onKeyDown: (e) => {
          if (props.onKeyDown) props.onKeyDown(e);
          if (e.defaultPrevented) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            (props.onClick as (ev: unknown) => void)(e);
            return;
          }
          /* Les FLÈCHES parcourent la liste ; `tr[tabindex]` écarte les rangées de détail. */
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const ligne = e.currentTarget;
            const corpsTable = ligne.parentElement;
            if (!corpsTable) return;
            const rangeesFocales = [...corpsTable.querySelectorAll('tr[tabindex]')] as HTMLElement[];
            const i = rangeesFocales.indexOf(ligne);
            const cible = rangeesFocales[i + (e.key === 'ArrowDown' ? 1 : -1)];
            if (cible) { e.preventDefault(); cible.focus(); }
          }
        },
      };
      const k = cleLigne(l);
      const rang = table.getRow(k, true);
      return (
        <React.Fragment key={k}>
          <tr {...props} {...auClavier} data-index={index} ref={virtualiser ? virt.measureElement : undefined}>
            {avecSelection && (
              <td style={{ ...celluleBase, width: LARGEUR_SELECTION, paddingInlineEnd: 0, ...styleSelection(false) }}>
                <SansOuvrir>
                  <input type="checkbox" checked={rang.getIsSelected()} onChange={rang.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ${k}`} style={{ margin: 0 }} />
                </SansOuvrir>
              </td>
            )}
            {visibles.map((c) => (
              <td key={c.cle} style={{
                ...celluleBase, ...(c.type === 'nombre' ? nombre : {}), ...celluleSouple(c), ...c.style,
                ...(fixe ? { whiteSpace: 'normal', overflowWrap: 'anywhere', paddingInline: `calc(${j.padX} / 3)` } : {}),
                ...styleEpingle(c.cle, false),
              }}>
                {c.rendu(l)}
              </td>
            ))}
            {actions && (
              <td style={{ ...celluleBase, whiteSpace: 'nowrap' }}>
                {cliquable ? <SansOuvrir>{actions(l)}</SansOuvrir> : actions(l)}
              </td>
            )}
          </tr>
          {/* `colSpan` sur les colonnes VISIBLES (+ sélection + actions) : une
              colonne masquée ferait déborder le détail d'une cellule. */}
          {deplie !== null && deplie !== false && deplie !== undefined && (
            <tr data-detail="">
              <td colSpan={colSpanDetail(visibles.length, { selection: avecSelection, actions: !!actions })} style={{ ...celluleBase, padding: 0, height: 'auto' }}>{deplie}</td>
            </tr>
          )}
        </React.Fragment>
      );
    };

    const filtreDe = (c: ColonneTriable<L>) => filtresActifs[c.cle];
    const ouvrirFiltre = (cle: string) => setMenu((m) => (m === `filtre:${cle}` ? null : `filtre:${cle}`));
    const poserFiltre = (cle: string, f: FiltreColonne | null) => {
      setFiltres((fs) => {
        const suivant = { ...fs };
        if (f && filtreActif(f)) suivant[cle] = f; else delete suivant[cle];
        return suivant;
      });
      // La sélection portait sur des lignes qui ne sont peut-être plus visibles.
      if (avecSelection) table.resetRowSelection();
    };

    const panneauFiltre = (c: ColonneTriable<L>) => {
      const f = filtres[c.cle];
      const type = c.type ?? 'texte';
      const titre = titreDe(c);
      return (
        <div data-panneau="" role="dialog" aria-label={`Filtrer ${titre}`} style={{ ...panneau, insetInlineStart: 0 }}
          onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} draggable={false}>
          <div style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 8 }}>Filtrer « {titre} »</div>
          {type === 'texte' && (
            <input autoFocus type="search" value={f?.type === 'texte' ? f.q : ''} placeholder="contient…" style={champ}
              aria-label={`${titre} contient`}
              onChange={(e) => poserFiltre(c.cle, { type: 'texte', q: e.target.value })} />
          )}
          {type === 'liste' && c.valeur && (
            <div style={{ maxHeight: 240, overflowY: 'auto', display: 'grid', gap: 4 }}>
              {valeursDistinctes(lignes, c.valeur).map((v) => {
                const coche = f?.type === 'liste' && f.valeurs.includes(v);
                return (
                  <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={coche} style={{ margin: 0 }}
                      onChange={() => {
                        const actuelles = f?.type === 'liste' ? f.valeurs : [];
                        poserFiltre(c.cle, { type: 'liste', valeurs: coche ? actuelles.filter((x) => x !== v) : [...actuelles, v] });
                      }} />
                    <span>{v === '' ? <em style={{ color: 'var(--text-muted)' }}>(vide)</em> : (c.libelleValeur ? c.libelleValeur(v) : v)}</span>
                  </label>
                );
              })}
            </div>
          )}
          {type === 'date' && (
            /* `dir="ltr"` : un champ de date s'écrit toujours de gauche à droite, même en
               page arabe (`qualite-portrait-catalogue` le vérifie sur manuel et site). */
            <div style={{ display: 'grid', gap: 6 }}>
              <label style={{ display: 'grid', gap: 2 }}>du
                <input type="date" dir="ltr" value={f?.type === 'date' ? f.de ?? '' : ''} style={champ}
                  onChange={(e) => poserFiltre(c.cle, { type: 'date', de: e.target.value || undefined, a: f?.type === 'date' ? f.a : undefined })} />
              </label>
              <label style={{ display: 'grid', gap: 2 }}>au
                <input type="date" dir="ltr" value={f?.type === 'date' ? f.a ?? '' : ''} style={champ}
                  onChange={(e) => poserFiltre(c.cle, { type: 'date', de: f?.type === 'date' ? f.de : undefined, a: e.target.value || undefined })} />
              </label>
            </div>
          )}
          {type === 'nombre' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <label style={{ display: 'grid', gap: 2 }}>min
                <input type="number" value={f?.type === 'nombre' && f.min !== undefined ? f.min : ''} style={champ}
                  onChange={(e) => poserFiltre(c.cle, { type: 'nombre', min: e.target.value === '' ? undefined : Number(e.target.value), max: f?.type === 'nombre' ? f.max : undefined })} />
              </label>
              <label style={{ display: 'grid', gap: 2 }}>max
                <input type="number" value={f?.type === 'nombre' && f.max !== undefined ? f.max : ''} style={champ}
                  onChange={(e) => poserFiltre(c.cle, { type: 'nombre', min: f?.type === 'nombre' ? f.min : undefined, max: e.target.value === '' ? undefined : Number(e.target.value) })} />
              </label>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10 }}>
            {filtreActif(f) && <Bouton taille="sm" variante="discret" onClick={() => poserFiltre(c.cle, null)}>Effacer</Bouton>}
            <Bouton taille="sm" onClick={() => setMenu(null)}>Fermer</Bouton>
          </div>
        </div>
      );
    };

    const lignesRendues = items
      ? items.map((it) => rendreLigne(rangees[it.index]!.original, it.index))
      : rangees.map((r, i) => rendreLigne(r.original, i));

    return (
      <>
        {/* ⚠️ SUR UN TÉLÉPHONE, LE TABLEAU DÉFILE DANS SA BOÎTE (Ines, 19/10/2026).
            Hors boîte, rien ne bornait sa largeur : sept colonnes élargissaient
            la PAGE entière, Safari la dézoomait pour la faire tenir, et la barre
            de connexion ne couvrait plus que la moitié de l'écran (« Catalogue
            des produits »). Sous 980 px, la boîte défile donc de côté ; la
            colonne épinglée reste visible, l'en-tête cesse de coller (voir
            FEUILLE_TABLEAU). Au-dessus, rien ne change. */}
        <style href="bio-tableau" precedence="default">{FEUILLE_TABLEAU}</style>
        <div ref={boiteRef} className={enBoite ? undefined : 'bio-tableau-boite'} style={{ overflow: enBoite ? 'auto' : 'visible', maxHeight: hauteurMax, borderRadius: 'inherit' }}>
          <table className="ged-tableau" ref={enBoite ? undefined : enTeteCollant}
            style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, ...(fixe ? { tableLayout: 'fixed' } : {}) }}>
            <thead><tr>
              {avecSelection && (
                <th scope="col" style={{ ...enTeteBase, top: 'var(--haut-entete, 0px)', width: LARGEUR_SELECTION, paddingInlineEnd: 0, ...styleSelection(true) }}>
                  <input type="checkbox" style={{ margin: 0 }}
                    checked={table.getIsAllPageRowsSelected()}
                    ref={(el) => { if (el) el.indeterminate = table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected(); }}
                    onChange={table.getToggleAllPageRowsSelectedHandler()}
                    aria-label="Sélectionner toutes les lignes visibles" />
                </th>
              )}
              {visibles.map((c) => {
                const actif = triCourant?.id === c.cle;
                const triable = cap.tri && !!c.valeur;
                const filtrable = montrerFiltres && !!c.valeur && c.filtrable !== false;
                const fActif = !!filtreDe(c);
                const epinglee = epinglees.includes(c.cle);
                return (
                  <th key={c.cle} scope="col" data-cle={c.cle} ref={refEntete(c.cle)}
                    draggable={cap.reordonner}
                    onDragStart={cap.reordonner ? () => setPortee(c.cle) : undefined}
                    onDragEnd={() => { setPortee(null); setSurvolee(null); }}
                    onDragOver={(e) => { if (portee && portee !== c.cle) { e.preventDefault(); setSurvolee(c.cle); } }}
                    onDrop={(e) => { e.preventDefault(); if (portee) changerPref(deposerColonne(cles, prefRef.current, portee, c.cle)); setPortee(null); setSurvolee(null); }}
                    /* `'none'` et non `undefined` sur une colonne triable non triée (É-7) :
                       sans l'attribut, un lecteur d'écran n'annonce pas la colonne comme TRIABLE. */
                    aria-sort={!triable ? undefined : actif ? (triCourant!.desc ? 'descending' : 'ascending') : 'none'}
                    style={{
                      ...enTeteBase, top: 'var(--haut-entete, 0px)', ...(c.type === 'nombre' ? nombre : {}),
                      ...(souple ? { whiteSpace: 'normal' } : {}), ...c.style,
                      ...(fixe ? { whiteSpace: 'normal', overflowWrap: 'normal', hyphens: 'auto', letterSpacing: 0, paddingInline: `calc(${j.padX} / 3)`, fontSize: 'var(--tableau-entete-compacte-police)' } : {}),
                      cursor: triable ? 'pointer' : cap.reordonner ? 'grab' : 'default',
                      outline: survolee === c.cle ? '2px solid var(--color-primary)' : undefined,
                      outlineOffset: -2,
                      opacity: portee === c.cle ? 0.5 : 1,
                      /* La largeur RÉGLÉE l'emporte : `width` seul n'est qu'une suggestion, `min` et `max` la rendent ferme. */
                      ...(pref.largeurs[c.cle]
                        ? { width: pref.largeurs[c.cle], minWidth: pref.largeurs[c.cle], maxWidth: pref.largeurs[c.cle] }
                        : null),
                      ...styleEpingle(c.cle, true),
                      position: 'sticky',
                    }}
                    title={[
                      c.aide ?? null,
                      triable ? 'Cliquer : trier' : null,
                      cap.reordonner ? 'glisser : déplacer la colonne' : null,
                      cap.redimensionner ? 'tirer le bord : régler la largeur' : null,
                    ].filter(Boolean).join(' · ') || undefined}
                    onClick={triable ? () => table.getColumn(c.cle)?.toggleSorting() : undefined}>
                    {cap.redimensionner && (
                      /* LA POIGNÉE DE LARGEUR — `stopPropagation` sur `mousedown` : sans lui le
                         geste démarrerait AUSSI le glisser-déposer. Double-clic : largeur automatique. */
                      <span draggable={false}
                        onMouseDown={(e) => {
                          e.stopPropagation(); e.preventDefault();
                          const th = e.currentTarget.parentElement as HTMLElement | null;
                          glisse.current = { cle: c.cle, x0: e.clientX, l0: th?.offsetWidth || LARGEUR_MIN };
                          document.body.style.cursor = 'col-resize';
                          document.body.style.userSelect = 'none';
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          const reste = { ...prefRef.current.largeurs };
                          delete reste[c.cle];
                          changerPref({ ...prefRef.current, largeurs: reste });
                        }}
                        title="Tirer pour régler la largeur · double-clic pour la rendre automatique"
                        style={{ position: 'absolute', insetInlineEnd: 0, top: 0, bottom: 0, width: 7, cursor: 'col-resize', zIndex: 1 }} />
                    )}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, ...(c.type === 'nombre' ? { flexDirection: 'row-reverse' } : {}) }}>
                      {c.libelle}
                      {epinglee && <Icon name="epingle" size={11} style={{ opacity: 0.7 }} />}
                      {triable && (
                        /* UNE SEULE flèche (RQ, 19/08/2026) ; une colonne non triée n'annonce pas de SENS (SYS-3) :
                           ⇅ dit « triable », ▲▼ disent « trié, et dans ce sens ». */
                        <span aria-hidden style={{ fontSize: actif ? '0.8em' : '0.9em', lineHeight: 1, color: actif ? 'var(--color-primary)' : 'var(--text-faint, #B9C2CC)' }}>
                          {actif ? (triCourant!.desc ? '▼' : '▲') : '⇅'}
                        </span>
                      )}
                      {filtrable && (
                        <button type="button" data-ouvre-panneau="" draggable={false}
                          aria-label={`Filtrer ${titreDe(c)}`} aria-expanded={menu === `filtre:${c.cle}`} aria-pressed={fActif}
                          onClick={(e) => { e.stopPropagation(); ouvrirFiltre(c.cle); }}
                          onMouseDown={(e) => e.stopPropagation()}
                          style={{
                            border: 0, background: fActif ? 'var(--cyan-50)' : 'transparent', color: fActif ? 'var(--color-primary)' : 'var(--text-muted)',
                            borderRadius: 'var(--radius-xs)', padding: 2, cursor: 'pointer', display: 'inline-flex', opacity: fActif ? 1 : 0.6,
                          }}>
                          <Icon name="filter" size={11} />
                        </button>
                      )}
                    </span>
                    {menu === `filtre:${c.cle}` && panneauFiltre(c)}
                  </th>
                );
              })}
              {actions && (
                <th scope="col" style={{ ...enTeteBase, top: 'var(--haut-entete, 0px)', position: 'sticky' }}>Actions</th>
              )}
            </tr></thead>
            <tbody ref={corpsRef}>
              {items && hautAvant > 0 && <tr aria-hidden style={{ height: hautAvant }}><td colSpan={colSpan} style={{ padding: 0, border: 0 }} /></tr>}
              {lignesRendues}
              {items && hautApres > 0 && <tr aria-hidden style={{ height: hautApres }}><td colSpan={colSpan} style={{ padding: 0, border: 0 }} /></tr>}
              {total === 0 && nbFiltres > 0 && (
                <tr>
                  <td colSpan={colSpan} style={{ ...celluleBase, textAlign: 'center', color: 'var(--text-muted)', whiteSpace: 'normal' }}>
                    Aucune ligne ne correspond aux filtres.
                    {' '}
                    <Bouton taille="sm" variante="discret" onClick={() => { setFiltres({}); setRecherche(''); }}>Réinitialiser</Bouton>
                  </td>
                </tr>
              )}
            </tbody>
            {pied && total > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 700, background: 'var(--gray-25, var(--surface-sunken))' }}>
                  {avecSelection && <td style={{ ...celluleBase, ...styleSelection(false) }} />}
                  {visibles.map((c) => (
                    <td key={c.cle} style={{ ...celluleBase, ...(c.type === 'nombre' ? nombre : {}), ...c.style, ...styleEpingle(c.cle, false) }}>
                      {pied(c, lignesFiltrees)}
                    </td>
                  ))}
                  {actions && <td style={celluleBase} />}
                </tr>
              </tfoot>
            )}
          </table>
          {/* La barre est DANS le conteneur du tableau : elle appartient à ce tableau-là. */}
          {parPage && (
            <PaginationTableau page={pagination.pageIndex + 1} pages={nbPages}
              debut={total === 0 ? 0 : pagination.pageIndex * parPage + 1}
              fin={Math.min((pagination.pageIndex + 1) * parPage, total)}
              total={total} onPage={(p) => table.setPageIndex(p - 1)} quoi={quoi} />
          )}
        </div>
      </>
    );
  })();

  /* ═════════════════════════════ Barre d'outils ═══════════════════════════ */
  const menuColonnes = menu === 'colonnes' && (
    <div data-panneau="" role="dialog" aria-label="Colonnes du tableau" style={{ ...panneau, insetInlineEnd: 0, minWidth: 260 }}>
      <div style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 8 }}>Colonnes</div>
      <div style={{ display: 'grid', gap: 2, maxHeight: 320, overflowY: 'auto' }}>
        {colonnesOrdonnees(cles, pref).map((k, i, ordre) => {
          const c = parCle.get(k);
          if (!c) return null;
          const obligatoire = obligatoires.includes(k);
          const masquee = pref.masquees.includes(k) && !obligatoire;
          return (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: obligatoire ? 'default' : 'pointer', color: obligatoire ? 'var(--text-muted)' : undefined }}>
                <input type="checkbox" checked={!masquee} disabled={obligatoire} style={{ margin: 0 }}
                  onChange={() => changerPref(basculerColonne(cles, prefRef.current, k, obligatoires))} />
                <span>{titreDe(c)}{obligatoire && <span style={{ color: 'var(--text-faint, var(--text-muted))' }}> · obligatoire</span>}</span>
              </label>
              {peutRegler && !c.obligatoire && !(colonnesObligatoires ?? []).includes(k) && (
                /* LA RQ IMPOSE DEPUIS LE TABLEAU (L1) : une colonne cadenassée ne se masque
                   pour personne. Le réglage vit dans `ged-setting.tableaux`. */
                <Bouton taille="sm" variante={obligatoiresServeur.includes(k) ? 'primaire' : 'discret'} icone="lock"
                  aria-label={obligatoiresServeur.includes(k) ? `Ne plus imposer ${titreDe(c)}` : `Imposer ${titreDe(c)} à tout le monde`}
                  aria-pressed={obligatoiresServeur.includes(k)}
                  onClick={() => {
                    const suivant = obligatoiresServeur.includes(k) ? obligatoiresServeur.filter((x) => x !== k) : [...obligatoiresServeur, k];
                    depotPrefs().imposer(id, suivant, cles).then(setObligatoiresServeur).catch(() => { /* la RQ réessaiera */ });
                  }} />
              )}
              {cap.epingler && (
                <Bouton taille="sm" variante={c.epinglee || pref.epinglees.includes(k) ? 'primaire' : 'discret'} icone="epingle"
                  aria-label={c.epinglee ? `${titreDe(c)} est épinglée par le registre` : pref.epinglees.includes(k) ? `Désépingler ${titreDe(c)}` : `Épingler ${titreDe(c)} à gauche`}
                  aria-pressed={c.epinglee || pref.epinglees.includes(k)}
                  disabled={!!c.epinglee} title={c.epinglee ? 'Épinglée par le registre' : undefined}
                  onClick={() => changerPref(basculerEpingle(cles, prefRef.current, k))} />
              )}
              {cap.reordonner && (
                <>
                  <Bouton taille="sm" variante="discret" aria-label={`Monter ${titreDe(c)}`} disabled={i === 0}
                    onClick={() => changerPref(deplacerColonne(cles, prefRef.current, k, -1))}>▲</Bouton>
                  <Bouton taille="sm" variante="discret" aria-label={`Descendre ${titreDe(c)}`} disabled={i === ordre.length - 1}
                    onClick={() => changerPref(deplacerColonne(cles, prefRef.current, k, 1))}>▼</Bouton>
                </>
              )}
            </div>
          );
        })}
      </div>
      {peutRegler && (
        <div style={{ color: 'var(--text-muted)', marginTop: 8 }}>
          Cadenas : colonne imposée à tout le monde (réglage du laboratoire).
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10 }}>
        {estPersonnalisee(cles, pref) && !auDefaut(cles, pref, masqueesDefaut) && (
          <Bouton taille="sm" variante="discret" onClick={() => { changerPref({ ...PREF_VIDE, masquees: [...masqueesDefaut] }); setTri([]); }}>Rétablir</Bouton>
        )}
        <Bouton taille="sm" onClick={() => setMenu(null)}>Fermer</Bouton>
      </div>
    </div>
  );

  const puces = [
    ...Object.entries(filtresActifs).map(([k, f]) => ({ k, texte: decrireFiltre(titreDe(parCle.get(k)!), f, jourLisible, parCle.get(k)!.libelleValeur), retirer: () => poserFiltreHors(k) })),
    ...(recherche.trim() ? [{ k: '__q', texte: `Recherche « ${recherche.trim()} »`, retirer: () => setRecherche('') }] : []),
  ];
  function poserFiltreHors(k: string) {
    setFiltres((fs) => { const s = { ...fs }; delete s[k]; return s; });
    if (avecSelection) table.resetRowSelection();
  }

  const barreOutils = barre && !chargement && !erreur && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: `${j.padY} ${j.padX}`, borderBottom: '1px solid var(--border-subtle, var(--border-default))', fontSize: 'var(--tableau-ligne-police)', color: 'var(--text-body)' }}>
      {montrerRecherche && (
        <div style={{ position: 'relative', flex: '0 1 260px' }}>
          <Icon name="search" size={13} style={{ position: 'absolute', insetInlineStart: 8, top: 8, color: 'var(--text-muted)' }} />
          <input type="search" value={recherche} onChange={(e) => { setRecherche(e.target.value); if (avecSelection) table.resetRowSelection(); }}
            placeholder={typeof rechercheProp === 'string' ? rechercheProp : 'Rechercher…'} aria-label="Rechercher dans le tableau"
            style={{ ...champ, paddingInlineStart: 26 }} />
        </div>
      )}
      {puces.map((p) => (
        <span key={p.k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 4px 2px 9px', borderRadius: 'var(--radius-pill)', background: 'var(--cyan-50)', color: 'var(--text-strong)' }}>
          {p.texte}
          <button type="button" onClick={p.retirer} aria-label={`Retirer le filtre : ${p.texte}`}
            style={{ border: 0, background: 'transparent', cursor: 'pointer', display: 'inline-flex', padding: 2, color: 'var(--text-muted)' }}>
            <Icon name="x" size={11} />
          </button>
        </span>
      ))}
      {nbFiltres > 0 && (
        <>
          <span style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>{total} / {lignes.length} {quoi ?? 'lignes'}</span>
          <Bouton taille="sm" variante="discret" onClick={() => { setFiltres({}); setRecherche(''); if (avecSelection) table.resetRowSelection(); }}>Réinitialiser</Bouton>
        </>
      )}
      {selectionnees.length > 0 && !confirmation && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)' }}>
          <strong>{selectionnees.length}</strong> sélectionnée{selectionnees.length > 1 ? 's' : ''}
          {actionsLot!.map((a) => (
            <Bouton key={a.cle} taille="sm" onClick={() => { setBilan(null); setMotifLot(a.motif?.defaut ?? ''); setConfirmation({ action: a, lignes: selectionnees.filter((l) => !a.applicable || a.applicable(l)) }); }}>{a.libelle}</Bouton>
          ))}
          <Bouton taille="sm" variante="discret" onClick={() => table.resetRowSelection()}>Annuler la sélection</Bouton>
        </span>
      )}
      {confirmation && (
        /* La confirmation NOMME le nombre : « Archiver 12 documents ? » (PROMPT-TABLEAUX §1). */
        <span role="alertdialog" aria-live="assertive" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '4px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong, var(--border-default))' }}>
          <strong>{libelleConfirmationLot(confirmation.action.libelle, confirmation.lignes.length, confirmation.action.quoi)}</strong>
          {confirmation.action.motif && (
            /* L10 : le MOTIF du lot, commun aux lignes et journalisé sur chacune. Le bouton
               reste fermé tant qu'il est trop court — la règle est celle de l'action, pas de l'écran. */
            <input type="text" value={motifLot} onChange={(e) => setMotifLot(e.target.value)}
              aria-label={confirmation.action.motif.libelle} placeholder={`${confirmation.action.motif.libelle} (${confirmation.action.motif.min ?? 10} caractères au moins)`}
              aria-invalid={!motifLotValide(confirmation.action, motifLot)}
              style={{ flex: '1 1 260px', minWidth: 200, height: 30, padding: '0 8px', font: 'inherit', fontSize: 'var(--tableau-ligne-police)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)', color: 'var(--text-body)' }} />
          )}
          <Bouton taille="sm" onClick={() => setConfirmation(null)} disabled={lotEnCours}>Annuler</Bouton>
          <Bouton taille="sm" variante="primaire" onClick={lancerLot} chargement={lotEnCours} libelleChargement="En cours…"
            disabled={!motifLotValide(confirmation.action, motifLot)}
            title={motifLotValide(confirmation.action, motifLot) ? undefined : `Un motif d’au moins ${confirmation.action.motif?.min ?? 10} caractères est exigé.`}>
            {confirmation.action.libelle}
          </Bouton>
        </span>
      )}
      {bilan && (
        /* Le bilan dit ce qui est passé ET ce qui ne l'est pas, ligne par ligne. */
        <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '4px 8px', borderRadius: 'var(--radius-sm)', background: bilan.bilan.echecs.length ? 'var(--red-50)' : 'var(--surface-sunken)' }}>
          <strong>{bilan.action.libelle} : {libelleBilan(bilan.bilan)}</strong>
          {bilan.bilan.echecs.length > 0 && (
            <span style={{ color: 'var(--red-600)' }}>{bilan.bilan.echecs.map((e) => `${e.cle} (${e.erreur})`).join(' · ')}</span>
          )}
          <Bouton taille="sm" variante="discret" onClick={() => setBilan(null)}>Fermer</Bouton>
        </span>
      )}
      <span style={{ flex: 1 }} />
      {montrerExport && (
        <Bouton taille="sm" icone="download" onClick={exporter} disabled={total === 0 || colonnesExport.length === 0}
          title={colonnesExport.length === 0 ? 'Aucune colonne déclarée « export: true »' : `Exporter les ${total} lignes filtrées (CSV)`}>
          Exporter (CSV)
        </Bouton>
      )}
      {montrerColonnes && (
        <span style={{ position: 'relative' }}>
          <Bouton taille="sm" icone="columns" data-ouvre-panneau="" aria-expanded={menu === 'colonnes'} aria-haspopup="dialog"
            aria-label={nbMasquees ? `Colonnes du tableau (${nbMasquees} masquée${nbMasquees > 1 ? 's' : ''})` : 'Colonnes du tableau'}
            title="Colonnes : afficher, masquer, déplacer"
            onClick={() => setMenu((m) => (m === 'colonnes' ? null : 'colonnes'))} />
          {menuColonnes}
        </span>
      )}
    </div>
  );

  return (
    /* `minWidth: 0` : dans une grille ou une rangée flexible, le tableau
       accepte de rétrécir à la largeur de l'écran au lieu de l'imposer. */
    <div ref={racine} style={{ position: 'relative', minWidth: 0, maxWidth: '100%' }}>
      {barreOutils}
      {corps}
    </div>
  );
}
